import { z } from 'zod'

import { http, isAppError } from '@/shared/api'

import { goauthHttp } from '@/features/auth'

import type {
  AdminUserDetail,
  AdminUserFilters,
  AdminUserListItem,
  AdminUserPage,
  AdminUserSession,
  AuditEntry,
  CreateUserInput,
  UpdateUserInput,
  UserStatus,
} from '../model/types'

/**
 * Two services answer this screen, and each answers for what it owns.
 *
 * **Identity is goauth's** — the name, the address, the role, the status. It is
 * where every mutation below is addressed, and now where the reads go too. It
 * used to read from api-service, which keeps a mirror of that truth fed by
 * goauth's webhooks, and that is a copy: the panel wrote to the original and
 * read the copy, so a save landed and the screen went on showing the old value
 * until the webhook caught up a second later. Measured — `PATCH` returned
 * `username: "…qwwe"` while the `GET` a moment later still said `"…qww"`.
 *
 * **The subscription, the plan and the campaign count are ours**, and goauth
 * knows nothing about them. They come from api-service, keyed by the same id.
 *
 * The mirror keeps its job — it is what lets our own tables carry a `user_id`
 * and join an email onto a campaign or an invoice. It is for linking, not for
 * showing.
 */

const statusSchema = z.enum(['active', 'blocked', 'deleted'])

/**
 * goauth's shape. `is_admin` rather than a role, and the two identifiers are
 * absent rather than empty on an account that was soft-deleted.
 */
const identitySchema = z.object({
  id: z.string(),
  username: z.string().nullish(),
  email: z.string().nullish(),
  is_admin: z.boolean(),
  status: statusSchema,
  created_at: z.string(),
})

const identityListSchema = z.object({
  users: z.array(identitySchema),
  total: z.number(),
})

/**
 * Our half, and nothing else — api-service stopped sending the identity columns
 * it kept a copy of, so there is no second answer about the same person to
 * decide between.
 */
const ourFieldsSchema = z.object({
  user_id: z.string(),
  sub_status: z.string(),
  plan_name: z.string(),
})

const ourListSchema = z.object({ items: z.array(ourFieldsSchema) })

const ourDetailSchema = z.object({
  sub_status: z.string(),
  plan_name: z.string(),
  sub_current_period_end: z.string().optional(),
})

type Identity = z.infer<typeof identitySchema>
type OurFields = { sub_status: string; plan_name: string }

const EMPTY_OURS: OurFields = { sub_status: '', plan_name: '' }

const toListItem = (identity: Identity, ours: OurFields): AdminUserListItem => ({
  id: identity.id,
  // A soft-deleted account has neither for good — goauth clears both.
  username: identity.username ?? '',
  email: identity.email ?? '',
  role: identity.is_admin ? 'super_admin' : 'user',
  status: identity.status,
  createdAt: identity.created_at,
  subStatus: ours.sub_status,
  planName: ours.plan_name,
})

export async function fetchAdminUsers(
  filters: AdminUserFilters,
  signal?: AbortSignal,
): Promise<AdminUserPage> {
  // Empty values are dropped by the facade, and both services read an absent
  // `status` as "every live account" — which is what an unset chip means.
  const searchParams = {
    q: filters.query,
    status: filters.status,
    limit: filters.limit,
    offset: filters.offset,
  }

  const [identity, ours] = await Promise.all([
    goauthHttp.get('admin/users', { searchParams, schema: identityListSchema, signal }),
    // Same window, so the two pages line up. A user goauth has and the mirror
    // has not yet heard of simply has no plan to show, which is a state this
    // screen already draws as a dash.
    http.get('admin/subscriptions', { searchParams, schema: ourListSchema, signal }),
  ])

  const byId = new Map(ours.items.map((item) => [item.user_id, item]))

  return {
    // goauth owns the rows and the count: it is the list, and the mirror is a
    // lookup table beside it.
    users: identity.users.map((user) => toListItem(user, byId.get(user.id) ?? EMPTY_OURS)),
    total: identity.total,
  }
}

export async function fetchAdminUser(id: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  const [identity, ours] = await Promise.all([
    goauthHttp.get(`admin/users/${id}`, { schema: identitySchema, signal }),
    /*
     * A 404 here is not an error. A user created a moment ago exists in goauth
     * and not yet in the mirror, and the card should open on what is known
     * rather than refuse over a subscription nobody has.
     */
    http
      .get(`admin/subscriptions/${id}`, { schema: ourDetailSchema, signal })
      .catch((error: unknown) => {
        if (isAppError(error) && error.status === 404) return null
        throw error
      }),
  ])

  return {
    ...toListItem(identity, ours ?? EMPTY_OURS),
    subCurrentPeriodEnd: ours?.sub_current_period_end ?? '',
  }
}

export async function createAdminUser(input: CreateUserInput): Promise<void> {
  await goauthHttp.post('admin/users', {
    json: {
      username: input.username,
      // Omitted rather than sent empty: goauth stores what it is given, and an
      // empty string is not the same as "no email".
      ...(input.email !== '' && { email: input.email }),
      password: input.password,
      is_admin: input.isAdmin,
      status: 'active',
      must_change_password: input.mustChangePassword,
    },
  })
}

/**
 * Every field is a pointer upstream, so an omitted key and a zeroed one are
 * different requests. An entirely empty patch is rejected as `invalid request`.
 */
export async function updateAdminUser(id: string, patch: UpdateUserInput): Promise<void> {
  await goauthHttp.patch(`admin/users/${id}`, {
    json: {
      ...(patch.username !== undefined && { username: patch.username }),
      ...(patch.email !== undefined && { email: patch.email }),
      ...(patch.status !== undefined && { status: patch.status }),
      ...(patch.isAdmin !== undefined && { is_admin: patch.isAdmin }),
    },
  })
}

/** Soft and idempotent: identifiers are cleared, the row stays, sessions die. */
export async function deleteAdminUser(id: string): Promise<void> {
  await goauthHttp.delete(`admin/users/${id}`)
}

/** Always forces a change on next sign-in and revokes every session the user has. */
export async function setAdminUserPassword(id: string, password: string): Promise<void> {
  await goauthHttp.post(`admin/users/${id}/password`, { json: { password } })
}

const adminSessionsSchema = z.object({
  sessions: z.array(
    z.object({
      id: z.string(),
      family_id: z.string(),
      ip: z.string().optional(),
      user_agent: z.string().optional(),
      created_at: z.string(),
      expires_at: z.string(),
    }),
  ),
})

export async function fetchAdminUserSessions(
  id: string,
  signal?: AbortSignal,
): Promise<AdminUserSession[]> {
  const raw = await goauthHttp.get(`admin/users/${id}/sessions`, {
    schema: adminSessionsSchema,
    signal,
  })

  return raw.sessions.map((session) => ({
    id: session.id,
    familyId: session.family_id,
    ip: session.ip ?? '',
    userAgent: session.user_agent ?? '',
    createdAt: session.created_at,
    expiresAt: session.expires_at,
  }))
}

export async function revokeAdminUserSessions(id: string): Promise<void> {
  await goauthHttp.delete(`admin/users/${id}/sessions`)
}

export const USER_STATUSES: UserStatus[] = ['active', 'blocked', 'deleted']

/**
 * Application audit, not identity audit — this one is api-service's, and it
 * records what the user did in the panel rather than what happened to their
 * account. goauth keeps its own log at `/admin/audit`.
 */
const auditItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  entity_type: z.string(),
  entity_id: z.string().optional(),
  old_value: z.unknown().optional(),
  new_value: z.unknown().optional(),
  ip_address: z.string().optional(),
  created_at: z.string(),
})

const auditSchema = z.array(auditItemSchema).nullable()

export async function fetchUserAudit(
  id: string,
  params: { limit: number; offset: number },
  signal?: AbortSignal,
): Promise<AuditEntry[]> {
  const raw = await http.get('admin/audit', {
    // The user is a parameter, not a segment: the log is ours, and all it knows
    // about a person is the id that acted.
    searchParams: { user_id: id, limit: params.limit, offset: params.offset },
    schema: auditSchema,
    signal,
  })

  return (raw ?? []).map((entry) => ({
    id: entry.id,
    action: entry.action,
    entityType: entry.entity_type,
    entityId: entry.entity_id ?? '',
    oldValue: entry.old_value,
    newValue: entry.new_value,
    ipAddress: entry.ip_address ?? '',
    createdAt: entry.created_at,
  }))
}
